import { Hymn } from "@/services/hymnsApi";
import { AlertOctagon, Loader2, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

interface DevDeleteMediaButtonProps {
  hymn: Hymn;
  sectionCode?: string;
  num: number;
  onDeleted?: () => void;
  className?: string;
}

export const DevDeleteMediaButton: React.FC<DevDeleteMediaButtonProps> = ({
  hymn,
  sectionCode,
  num,
  onDeleted,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 1. Vite Production Guard: Never render when deployed in production
  if (
    import.meta.env.PROD ||
    import.meta.env.MODE === "production" ||
    !import.meta.env.DEV
  ) {
    return null;
  }

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "[::1]");

  if (!isLocalhost || !hymn?.media_url) {
    return null;
  }

  const effectiveSection = sectionCode || hymn.section || "";
  const paddedNum = String(num).padStart(3, "0");

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch("/api/dev/delete-media", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          section: effectiveSection,
          songNumber: num,
          title: hymn.title,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Échec de la suppression");
      }

      toast.success(
        `Média supprimé avec succès ! Placeholder créé : ${data.placeholderCreated}`,
        {
          duration: 6000,
        },
      );
      setIsOpen(false);
      if (onDeleted) {
        onDeleted();
      }
    } catch (err: any) {
      console.error("Delete media error:", err);
      toast.error(`Erreur lors de la suppression: ${err.message || err}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Big prominent Dev-only Destructive Container */}
      <div
        className={` m-8 rounded-2xl border-2 border-dashed border-red-500/50 bg-red-950/25 p-2 sm:p-2 my-6 backdrop-blur-sm ${className}`}
      >
        <div className="m-1 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30 shrink-0 mt-0.5 sm:mt-0">
              <AlertOctagon className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded bg-red-500 text-white shadow-sm">
                  DEV ONLY
                </span>
                <span className="text-xs font-semibold text-red-300">
                  Environnement Local ({effectiveSection} #{paddedNum})
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-300 mt-0.5">
                Supprime Cloudflare R2 audio et génère{" "}
                <code className="px-1.5 py-0.5 rounded bg-slate-900 border border-slate-700 text-red-300 font-mono text-xs">
                  dist/media/{effectiveSection}/{paddedNum}_notfound.m4a
                </code>
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-gradient-to-r from-red-600 via-rose-600 to-red-700 hover:from-red-500 hover:to-rose-600 text-white font-black text-sm uppercase tracking-wider shadow-lg shadow-red-600/30 hover:shadow-red-600/50 transition-all active:scale-95 cursor-pointer border border-red-400/40"
          >
            <Trash2 className="w-5 h-5" />
            <span>DELETE</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 sm:pt-16 bg-black/80 backdrop-blur-sm animate-in fade-in overflow-y-auto">
          <div
            className="w-full max-w-lg rounded-3xl bg-slate-900 border-2 border-red-500/60 p-6 sm:p-8 shadow-2xl shadow-red-950/60 text-white space-y-5 animate-in zoom-in-95"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-red-500/20 border border-red-500/40 text-red-400">
                  <Trash2 className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-white">
                    Confirmer la suppression
                  </h3>
                  <p className="text-xs text-red-400 font-bold uppercase tracking-wider">
                    Action Irréversible
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !isDeleting && setIsOpen(false)}
                disabled={isDeleting}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 space-y-2 text-sm">
              <p className="font-semibold text-slate-200">
                Chant concerné :{" "}
                <span className="text-amber-400 font-bold">
                  N° {num} ({paddedNum}) - {hymn.title}
                </span>
              </p>
              {hymn.media_url && (
                <div className="text-xs text-slate-400 break-all pt-0.5">
                  <span className="font-semibold text-slate-300">URL cible : </span>
                  <a
                    href={hymn.media_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-400 hover:text-sky-300 font-mono underline underline-offset-2 break-all"
                  >
                    {hymn.media_url}
                  </a>
                </div>
              )}
              <p className="text-xs text-slate-400">
                Recueil :{" "}
                <span className="text-slate-300 font-medium">
                  {effectiveSection}
                </span>
              </p>
              <ul className="text-xs text-slate-300 space-y-1.5 pt-2 border-t border-slate-800 list-disc list-inside">
                <li>
                  Suppression sur{" "}
                  <strong className="text-red-300">Cloudflare R2</strong> via{" "}
                  <code className="text-slate-300">rclone delete</code>
                </li>
                <li>
                  Suppression du fichier audio local dans{" "}
                  <strong className="text-red-300">
                    dist/media/{effectiveSection}/
                  </strong>
                </li>
                <li>
                  Création vide:{" "}
                  <code className="text-emerald-400 font-bold">
                    dist/media/{effectiveSection}/{paddedNum}_notfound.m4a
                  </code>
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                disabled={isDeleting}
                className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-sm transition-colors cursor-pointer disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-sm shadow-md shadow-red-600/30 transition-all active:scale-95 cursor-pointer disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Suppression en cours...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Confirmer la suppression</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
